/**
 * Tegner et ferdig oppsett med jsPDF. Selve oppsettet regnes ut i oppsett.ts.
 */

import jsPDF from 'jspdf';
import { renderPDFHeader } from '../pdf/pdfHeader';
import { hexToRgb } from '../pdf/pdfUtils';
import { SIDE } from './oppsett';
import type { SangOppsett } from './oppsett';

export interface PdfHode {
  tittel: string;
  artist: string;
  toneart?: string;
}

const SVART: [number, number, number] = [0, 0, 0];
const FRIGI_URL_ETTER_MS = 60_000;

// Hvert tegn blir nøyaktig ett tegn, så teksten ikke forskyves mot akkordene.
const ERSTATNINGER: Record<string, string> = { '♯': '#', '♭': 'b', '→': '>', '\t': ' ' };
// ASCII, Latin-1 og tegnene WinAnsi har i tillegg (Windows-1252).
const WIN_ANSI = /^[\x20-\x7E\xA0-\xFF€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ]$/;

/** Courier og Helvetica i jsPDF kan bare skrive WinAnsi. Andre tegn ville blitt uleselige. */
function rensTekst(tekst: string): string {
  return Array.from(tekst.normalize('NFC'))
    .map((tegn) => ERSTATNINGER[tegn] ?? (WIN_ANSI.test(tegn) ? tegn : '?'))
    .join('');
}

export function tegnPdf(oppsett: SangOppsett, hode: PdfHode, akkordfarge: string): Blob {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const akkordRgb = hexToRgb(akkordfarge);

  renderPDFHeader(doc, SIDE.bredde, {
    title: rensTekst(hode.tittel),
    artist: rensTekst(hode.artist),
    transposedKey: hode.toneart && rensTekst(hode.toneart),
  });

  oppsett.sider.forEach((side, indeks) => {
    if (indeks > 0) doc.addPage();
    side.elementer.forEach((element) => {
      const [r, g, b] = element.type === 'akkord' ? akkordRgb : SVART;
      doc.setFont('courier', element.type === 'tekst' ? 'normal' : 'bold');
      doc.setFontSize(element.storrelse);
      doc.setTextColor(r, g, b);
      doc.text(rensTekst(element.tekst), element.x, element.y);
    });
  });

  return doc.output('blob');
}

export function aapnePdf(pdf: Blob): void {
  const url = URL.createObjectURL(pdf);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), FRIGI_URL_ETTER_MS);
}
